/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
// Custom rxjs operators that do things with the angular form can live here

import { forkJoin, Observable, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { UntypedFormGroup } from '@angular/forms';

/**
 * Accepts a form and a "resetNotifier". This creates a more granular version of the valueChanges observable that only notifies with
 * the data of what has changes. So if just the "activityName" field changes, the emission will only include the activityName change.
 * And not the current state of the entire form.
 * This has the fairly unique requirement of needing to explicitly maintain valueChanges subscription instances to avoid missing anything.
 * Implements as a fully custom creation operator as conditionally maintaining _some_ child subscriptions is difficult with standard operators.
 * @param resetNotifier A notifier observable, any emission on this observable will recalc the observable. This is largely to be used
 * when formControls are added and removed.
 *
 * Unit testing notes:
 * - validate all the completion and error cases, respect "the observable contract"
 * - normal emissions
 */
export function valueChangesOfChildren<T = any>(
	form: UntypedFormGroup,
	resetNotifier: Observable<void>
): Observable<{ [key: string]: T }> {
	return new Observable((subscriber) => {
		const activeSubscriptions = new Map<string, Subscription>();
		let allCompleteSubscription = new Subscription();

		//an error or complete fired, immediately unsub from the sources to respect "the observable contract"
		function abort() {
			resetSubscription.unsubscribe();
			activeSubscriptions.forEach((sub) => {
				sub.unsubscribe();
			});
			activeSubscriptions.clear();
			allCompleteSubscription.unsubscribe();
		}

		//reset notifier "nexted", re-evaluate the subscriptions and modify as needed
		function recalc() {
			for (const [name, control] of Object.entries(form.controls)) {
				if (!activeSubscriptions.has(name)) {
					const sub = control.valueChanges.subscribe({
						next: (value) => {
							subscriber.next({ [name]: value });
						},
						//complete is handled below
						error: (err) => {
							subscriber.error(err);
							abort();
						},
					});
					activeSubscriptions.set(name, sub);
				}
			}

			activeSubscriptions.forEach((cachedSubscription, name, srcMap) => {
				if (!form.contains(name)) {
					cachedSubscription.unsubscribe();
					srcMap.delete(name);
				}
			});

			//detect when every source valueChanges is complete, and complete the output observable when it happens
			if (allCompleteSubscription) {
				allCompleteSubscription.unsubscribe();
			}
			allCompleteSubscription = forkJoin(
				Object.entries(form.controls).map(([name, control]) => {
					return control.valueChanges;
				})
			).subscribe(() => {
				subscriber.complete();
				abort();
			});
		}

		const resetSubscription = resetNotifier.pipe(startWith(null as void)).subscribe({
			next: () => {
				recalc();
			},
			complete: () => {
				subscriber.complete();
				abort();
			},
			error: (err) => {
				subscriber.error(err);
				abort();
			},
		});
	});
}
